import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/shared/lib/utils";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import type { VenueListItem } from "./types";
import { formatDateShort, formatVenueLocation } from "./format-venue";
import { VenueStatusBadge } from "./VenueStatusBadge";
import { VenueTypeBadge } from "./VenueTypeBadge";

interface VenuesTableProps {
  readonly items: readonly VenueListItem[];
}

export function VenuesTable({ items }: VenuesTableProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      data-testid="venues-table"
      className="overflow-x-auto rounded-lg border border-border bg-card"
    >
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/30 text-left">
          <tr>
            <Th>{t("venuesDirectory.table.venue")}</Th>
            <Th>{t("venuesDirectory.table.type")}</Th>
            <Th>{t("venuesDirectory.table.status")}</Th>
            <Th>{t("venuesDirectory.table.owner")}</Th>
            <Th>{t("venuesDirectory.table.created")}</Th>
            <Th className="w-12">
              <span className="sr-only">{t("venuesDirectory.table.openDetail")}</span>
            </Th>
          </tr>
        </thead>
        <tbody>
          {items.map((venue) => (
            <tr
              key={venue.id}
              data-testid={`venue-row-${venue.id}`}
              onClick={() => navigate(`/venues-directory/${encodeURIComponent(venue.id)}`)}
              className="cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/40"
            >
              <Td>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-medium text-foreground">{venue.name}</span>
                    <TestPartitionBadge
                      isTest={venue.isTest}
                      testId={`venue-test-badge-${venue.id}`}
                    />
                  </div>
                  <span className="font-sans text-xs text-muted-foreground">
                    {formatVenueLocation(venue) || "—"}
                  </span>
                </div>
              </Td>
              <Td>
                <VenueTypeBadge type={venue.type} />
              </Td>
              <Td>
                <VenueStatusBadge status={venue.status} />
              </Td>
              <Td>
                <div className="flex flex-col">
                  <span className="font-sans text-foreground">{venue.ownerName || "—"}</span>
                  <span className="font-sans text-xs text-muted-foreground">
                    {venue.ownerEmail || "—"}
                  </span>
                </div>
              </Td>
              <Td className="text-muted-foreground">
                {formatDateShort(venue.createdAt, i18n.language) || "—"}
              </Td>
              <Td className="text-right">
                <ChevronRight
                  className="ml-auto h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "px-4 py-3 font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground",
        className,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  className,
}: {
  readonly children: React.ReactNode;
  readonly className?: string;
}) {
  return <td className={cn("px-4 py-3 align-middle", className)}>{children}</td>;
}
